import React from 'react';
import { format } from 'date-fns';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EventHeader = ({
  event,
  isRegistered,
  registering,
  onRegister,
  onCancelRegistration,
  isUpcoming,
  isPastDeadline,
  isFull
}) => {
  const getEventTypeIcon = (type) => {
    const iconMap = {
      workshop: 'Tool',
      conference: 'Users',
      webinar: 'Monitor',
      hackathon: 'Code',
      networking: 'MessageCircle',
      training: 'BookOpen',
      showcase: 'Award',
      meeting: 'Calendar'
    };
    return iconMap?.[type] || 'Calendar';
  };

  const getEventTypeColor = (type) => {
    const colorMap = {
      workshop: 'bg-blue-100 text-blue-800',
      conference: 'bg-purple-100 text-purple-800',
      webinar: 'bg-green-100 text-green-800',
      hackathon: 'bg-red-100 text-red-800',
      networking: 'bg-yellow-100 text-yellow-800',
      training: 'bg-indigo-100 text-indigo-800',
      showcase: 'bg-pink-100 text-pink-800',
      meeting: 'bg-gray-100 text-gray-800'
    };
    return colorMap?.[type] || 'bg-gray-100 text-gray-800';
  };

  const canRegister = isUpcoming && !isPastDeadline && !isFull && event?.registration_required;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Hero Image */}
      {event?.image_url && (
        <div className="w-full h-64 bg-muted overflow-hidden">
          <img
            src={event?.image_url}
            alt={event?.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventTypeColor(event?.event_type)}`}>
                <Icon name={getEventTypeIcon(event?.event_type)} size={12} className="mr-1" />
                {event?.event_type?.charAt(0)?.toUpperCase() + event?.event_type?.slice(1)}
              </span>
              {event?.is_featured && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <Icon name="Star" size={12} className="mr-1" />
                  Featured
                </span>
              )}
              {!isUpcoming && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <Icon name="Clock" size={12} className="mr-1" />
                  Past Event
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              {event?.title}
            </h1>

            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 text-sm text-muted-foreground space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} />
                <span>
                  {format(new Date(event?.start_date), 'EEEE, MMMM dd, yyyy')}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span>
                  {format(new Date(event?.start_date), 'h:mm a')}
                  {event?.end_date && ` - ${format(new Date(event?.end_date), 'h:mm a')}`}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name={event?.is_virtual ? "Monitor" : "MapPin"} size={16} />
                <span>{event?.is_virtual ? 'Virtual Event' : event?.location}</span>
              </div>
            </div>
          </div>

          {/* Registration Actions */}
          <div className="flex-shrink-0">
            {isUpcoming && (
              <div className="space-y-2">
                {isRegistered ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                      <Icon name="CheckCircle" size={16} />
                      <span>You're registered!</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onCancelRegistration}
                      disabled={registering}
                      className="w-full"
                    >
                      {registering ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-muted-foreground mr-2"></div>
                          Cancelling...
                        </>
                      ) : (
                        <>
                          <Icon name="X" size={16} className="mr-2" />
                          Cancel Registration
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {canRegister ? (
                      <Button
                        onClick={onRegister}
                        disabled={registering}
                        className="w-full"
                      >
                        {registering ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Registering...
                          </>
                        ) : (
                          <>
                            <Icon name="UserPlus" size={16} className="mr-2" />
                            Register Now
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="space-y-1">
                        {isPastDeadline && (
                          <p className="text-xs text-destructive text-center">Registration Closed</p>
                        )}
                        {isFull && (
                          <p className="text-xs text-destructive text-center">Event Full</p>
                        )}
                        {!event?.registration_required && (
                          <Button onClick={onRegister} className="w-full">
                            <Icon name="Info" size={16} className="mr-2" />
                            Learn More
                          </Button>
                        )}
                      </div>
                    )}

                    {event?.max_participants && (
                      <div className="text-xs text-center text-muted-foreground">
                        {event?.current_participants} / {event?.max_participants} registered
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isUpcoming && (
              <div className="text-sm text-muted-foreground text-center">
                <Icon name="Clock" size={16} className="mx-auto mb-1" />
                This event has ended
              </div>
            )}
          </div>
        </div>

        {/* Registration Deadline Warning */}
        {isUpcoming && event?.registration_deadline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-sm text-yellow-800">
              <Icon name="Clock" size={16} />
              <span>
                Registration deadline: {format(new Date(event?.registration_deadline), 'MMMM dd, yyyy')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventHeader;